import type {
  CreateFolderRequest,
  CreateFolderResponse,
  DeleteFolderResponse,
  Folder,
  GetThreadResponse,
  GrantPermissionRequest,
  ListAggregatedThreadsResponse,
  ListAllUsersResponse,
  ListContactsResponse,
  ListPermissionsResponse,
  ListThreadsResponse,
  MailboxMembership,
  MoveThreadRequest,
  ProvisionMailboxRequest,
  ProvisionMailboxResponse,
  RenameFolderRequest,
  RenameFolderResponse,
  RetrySendRequest,
  RevokePermissionResponse,
  SearchResponse,
  SendMessageRequest,
  SendMessageResponse,
  SetThreadUnreadRequest,
  SetThreadUnreadResponse,
  UndoSendRequest,
  UndoSendResponse,
  UpdateMailboxSettingsRequest,
  UpdateMailboxSettingsResponse,
  UploadAttachmentResponse,
  User,
} from "@onemileauto/shared-types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

/**
 * Phase 8 (password auth): every protected endpoint this file talks to now
 * sits behind the session-cookie gate (see apps/mail's lib/session-auth.ts)
 * instead of Cloudflare Access intercepting at the edge. That means a 401
 * can now genuinely reach the SPA (an expired/missing session) where it
 * never could before — Access always redirected before the app ever loaded.
 * A hard navigation to /login is the simplest correct response: it also
 * naturally discards any stale TanStack Query cache from the previous
 * session. /api/auth/* itself is called via lib/auth-client.ts, not this
 * file, specifically so THIS interceptor never fires for a login page's own
 * "wrong password" 401 — that needs inline error UI, not a redirect loop.
 */
function redirectToLoginOn401(status: number): void {
  if (status === 401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!res.ok) {
    redirectToLoginOn401(res.status);
    const body = await res.json().catch(() => ({}));
    throw new ApiError((body as { detail?: string }).detail ?? res.statusText, res.status);
  }
  return res.json() as Promise<T>;
}

export interface MeResponse {
  user: User;
  mailboxes: MailboxMembership[];
}

export function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/me");
}

export function listFolders(mailboxId: string): Promise<{ folders: Folder[] }> {
  return apiFetch(`/api/mailboxes/${mailboxId}/folders`);
}

/** Phase 6: folder CRUD. Gated server-side at "folder_admin" (owner/full) — see routes/folders.ts. System folders 400 on rename/delete regardless of what the UI shows. */
export function createFolder(mailboxId: string, name: string): Promise<CreateFolderResponse> {
  const body: CreateFolderRequest = { name };
  return apiFetch(`/api/mailboxes/${mailboxId}/folders`, { method: "POST", body: JSON.stringify(body) });
}

export function renameFolder(mailboxId: string, folderId: string, name: string): Promise<RenameFolderResponse> {
  const body: RenameFolderRequest = { name };
  return apiFetch(`/api/mailboxes/${mailboxId}/folders/${folderId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** Deletes a custom folder; any threads filed in it are moved to Archive server-side, not orphaned — see MailboxDO#deleteFolder. */
export function deleteFolder(mailboxId: string, folderId: string): Promise<DeleteFolderResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/folders/${folderId}`, { method: "DELETE" });
}

export function listThreads(
  mailboxId: string,
  folderSlug: string,
  cursor?: string | null,
): Promise<ListThreadsResponse> {
  const params = new URLSearchParams({ folder: folderSlug });
  if (cursor) params.set("cursor", cursor);
  return apiFetch(`/api/mailboxes/${mailboxId}/threads?${params.toString()}`);
}

export function getThread(mailboxId: string, threadId: string): Promise<GetThreadResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/threads/${threadId}`);
}

export function moveThread(mailboxId: string, threadId: string, folderId: string): Promise<{ ok: boolean }> {
  const body: MoveThreadRequest = { folderId };
  return apiFetch(`/api/mailboxes/${mailboxId}/threads/${threadId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function setThreadUnread(
  mailboxId: string,
  threadId: string,
  unread: boolean,
): Promise<SetThreadUnreadResponse> {
  const body: SetThreadUnreadRequest = { unread };
  return apiFetch(`/api/mailboxes/${mailboxId}/threads/${threadId}/read`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function searchMessages(mailboxId: string, q: string): Promise<SearchResponse> {
  const params = new URLSearchParams({ q });
  return apiFetch(`/api/mailboxes/${mailboxId}/search?${params.toString()}`);
}

/** Recipient autocomplete (see hooks/use-contacts.ts). An empty/blank `q` deliberately omits the query param entirely rather than sending `q=` — matches the server's "empty q means top recent/frequent contacts" contract exactly. */
export function listContacts(mailboxId: string, q: string): Promise<ListContactsResponse> {
  const trimmed = q.trim();
  const qs = trimmed ? `?${new URLSearchParams({ q: trimmed }).toString()}` : "";
  return apiFetch(`/api/mailboxes/${mailboxId}/contacts${qs}`);
}

export function attachmentUrl(mailboxId: string, attachmentId: string): string {
  return `/api/mailboxes/${mailboxId}/attachments/${attachmentId}`;
}

/**
 * Upload a new file to attach to an outbound message (see compose-sheet.tsx
 * / routes/attachments.ts). Deliberately doesn't go through apiFetch()
 * above — that helper always sets `Content-Type: application/json`, which
 * would break a multipart/form-data body (the browser needs to set that
 * header itself, boundary included, from the FormData instance).
 */
export async function uploadAttachment(mailboxId: string, file: File): Promise<UploadAttachmentResponse> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/mailboxes/${mailboxId}/attachments/upload`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    redirectToLoginOn401(res.status);
    const body = await res.json().catch(() => ({}));
    throw new ApiError((body as { detail?: string }).detail ?? res.statusText, res.status);
  }
  return res.json() as Promise<UploadAttachmentResponse>;
}

export function sendMessage(mailboxId: string, body: SendMessageRequest): Promise<SendMessageResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/messages/send`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Always resolves (never throws on "too late"/"not found" — those are expected outcomes the caller inspects via `ok`/`status`, not request failures). */
export function undoSend(mailboxId: string, outboxId: string): Promise<UndoSendResponse> {
  const body: UndoSendRequest = { outboxId };
  return apiFetch(`/api/mailboxes/${mailboxId}/messages/undo`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function retrySend(mailboxId: string, outboxId: string): Promise<SendMessageResponse> {
  const body: RetrySendRequest = { outboxId };
  return apiFetch(`/api/mailboxes/${mailboxId}/messages/retry`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---- Staff mailbox delegation (Phase 5): provisioning, ACL, aggregate view ----

export function provisionMailbox(body: ProvisionMailboxRequest): Promise<ProvisionMailboxResponse> {
  return apiFetch("/api/admin/mailboxes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listPermissions(mailboxId: string): Promise<ListPermissionsResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/permissions`);
}

export function grantPermission(mailboxId: string, body: GrantPermissionRequest): Promise<ListPermissionsResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/permissions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function revokePermission(mailboxId: string, userId: string): Promise<RevokePermissionResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/permissions/${userId}`, { method: "DELETE" });
}

export function listAggregatedThreads(folderSlug: string, cursor?: string | null): Promise<ListAggregatedThreadsResponse> {
  const params = new URLSearchParams({ folder: folderSlug });
  if (cursor) params.set("cursor", cursor);
  return apiFetch(`/api/threads?${params.toString()}`);
}

// ---- Signature / out-of-office + global accounts view (Phase 9) ----

/** Owner-only (requireMailbox("admin") server-side — see routes/mailboxes.ts). */
export function updateMailboxSettings(
  mailboxId: string,
  body: UpdateMailboxSettingsRequest,
): Promise<UpdateMailboxSettingsResponse> {
  return apiFetch(`/api/mailboxes/${mailboxId}/settings`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** Owner-only, system-wide (see routes/admin.ts). */
export function listAllUsers(): Promise<ListAllUsersResponse> {
  return apiFetch("/api/admin/users");
}
